import React, { useEffect, useState } from 'react';
import axios from '../../api/axios';
import Select from "react-select";
import { toast } from "react-toastify";
import Loader from '../../components/Loader';
import { ChevronDownIcon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import { useParams, useNavigate } from 'react-router-dom';
import AddCategoryBrandModal from '../../components/AddCategoryBrandModal';

const NewItem = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!itemId;

  // allow enabling both size & color independently
  const [trackBySize, setTrackBySize] = useState(true);   // keep legacy default
  const [trackByColor, setTrackByColor] = useState(false);

  const [item, setItem] = useState({
    sizes: [],
    colors: []
  });

  const [loading, setLoading] = useState(false);
  const [categoriesOption, setCategoriesOption] = useState([]);
  const [brandsOption, setBrandsOption] = useState([]);
  const [unitsOption, setUnitsOption] = useState([]);

  // NEW: meta colors (like sizes)
  const [colorsOption, setColorsOption] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [imagePreview, setImagePreview] = useState('');

  const DropdownIndicator = ({ innerProps }) => (
    <div {...innerProps}><ChevronDownIcon className="h-5 w-5 text-gray-400" /></div>
  );

  const asInt = (v, def = 0) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : def;
  };

  useEffect(() => {
    // meta
    axios.get('/meta/categories')
      .then(res => setCategoriesOption(res.data.map(c => ({ value: c.id, label: c.name }))));
    axios.get('/meta/brands')
      .then(res => setBrandsOption(res.data.map(b => ({ value: b.id, label: b.name }))));
    axios.get('/meta/units')
      .then(res => setUnitsOption(res.data.map(u => ({ value: u.name, label: u.name }))));

    // fetch meta sizes + meta colors
    const sizesPromise = axios.get('/meta/sizes');
    const colorsPromise = axios.get('/meta/colors');

    if (isEditMode) {
      // edit mode: load the item itself
      axios.get(`/items/${itemId}`).then(res => {
        const data = res.data || {};
        setItem({
          ...data,
          sizes: Array.isArray(data.sizes) ? data.sizes : [],
          colors: Array.isArray(data.colors) ? data.colors : []
        });

        // infer which editors to show
        setTrackBySize(!!(data?.sizes?.length));
        setTrackByColor(!!(data?.colors?.length));
        if (data.image) setImagePreview(data.image);
      });

      // also load meta lists for suggestions/presets
      Promise.all([sizesPromise, colorsPromise]).then(([sizesRes, colorsRes]) => {
        const sizeOpts = (sizesRes.data || []).map(s => s.name);
        setMetaDefaultSizes(sizeOpts);

        const colorOpts = (colorsRes.data || []).map(c => c.name || c.color || c.hex_code || '');
        setColorsOption(colorOpts.filter(Boolean));
      });
    } else {
      // create: preload SKU + default sizes + default colors
      axios.get('/meta/latest-sku')
        .then(res => setItem(prev => ({ ...prev, sku: res.data.sku })));

      Promise.all([sizesPromise, colorsPromise]).then(([sizesRes, colorsRes]) => {
        const defaultSizes = (sizesRes.data || []).map(s => ({ size: s.name, quantity: 0 }));
        const defaultColors = (colorsRes.data || []).map(c => ({
          color: c.name || c.color || '',
          quantity: 0
        })).filter(c => c.color);

        setItem(prev => ({
          ...prev,
          sizes: defaultSizes,
          colors: defaultColors.length ? defaultColors : [{ color: '', quantity: 0 }]
        }));

        setTrackBySize(true);
        setTrackByColor(!!defaultColors.length);

        // keep meta options for suggestion buttons
        setMetaDefaultSizes((sizesRes.data || []).map(s => s.name));
        setColorsOption((colorsRes.data || []).map(c => c.name || c.color || '').filter(Boolean));
      });
    }
  }, [isEditMode, itemId]);

  // Keep a cached meta size names (for fillers button)
  const [metaDefaultSizes, setMetaDefaultSizes] = useState([]);

  // ----- Basic inputs -----
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setItem(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setItem(prev => ({ ...prev, image: reader.result }));
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // ===== Sizes editor =====
  const handleSizeChange = (index, field, value) => {
    const updated = [...(item.sizes || [])];
    updated[index] = {
      ...updated[index],
      [field]: field === 'quantity' ? asInt(value) : value
    };
    setItem(prev => ({ ...prev, sizes: updated }));
  };
  const addSizeRow = () =>
    setItem(prev => ({ ...prev, sizes: [...(prev.sizes || []), { size: '', quantity: 0 }] }));
  const removeSizeRow = (index) =>
    setItem(prev => ({ ...prev, sizes: (prev.sizes || []).filter((_, i) => i !== index) }));

  // Fill sizes from presets (meta)
  const usePresetSizes = () => {
    const filled = (metaDefaultSizes || []).map(n => ({ size: n, quantity: 0 }));
    setItem(prev => ({ ...prev, sizes: filled.length ? filled : [{ size: '', quantity: 0 }] }));
  };

  // ===== Colors editor =====
  const handleColorChange = (index, field, value) => {
    const updated = [...(item.colors || [])];
    updated[index] = {
      ...updated[index],
      [field]: field === 'quantity' ? asInt(value) : value
    };
    setItem(prev => ({ ...prev, colors: updated }));
  };
  const addColorRow = () =>
    setItem(prev => ({ ...prev, colors: [...(prev.colors || []), { color: '', quantity: 0 }] }));
  const removeColorRow = (index) =>
    setItem(prev => ({ ...prev, colors: (prev.colors || []).filter((_, i) => i !== index) }));

  // Fill colors from presets (meta)
  const usePresetColors = () => {
    const filled = (colorsOption || []).map(n => ({ color: n, quantity: 0 }));
    setItem(prev => ({ ...prev, colors: filled.length ? filled : [{ color: '', quantity: 0 }] }));
  };

  // ===== Submit =====
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // only send arrays that are enabled, and sanitize rows
      const payload = { ...item };

      if (trackBySize) {
        payload.sizes = (item.sizes || [])
          .filter(s => (s.size || '').trim().length)
          .map(s => ({ size: s.size.trim(), quantity: asInt(s.quantity) }));
      } else {
        delete payload.sizes;
      }

      if (trackByColor) {
        payload.colors = (item.colors || [])
          .filter(c => (c.color || '').trim().length)
          .map(c => ({ color: c.color.trim(), quantity: asInt(c.quantity) }));
      } else {
        delete payload.colors;
      }

      if (isEditMode) {
        await axios.put(`/items/${itemId}`, payload);
        toast.success('Item updated successfully!');
      } else {
        await axios.post(`/items`, payload);
        toast.success('Item created successfully!');
      }
      navigate('/items');
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || 'Failed to save item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative max-w-7xl mx-auto p-4 bg-white shadow rounded text-sm">
      <h2 className="text-xl font-semibold mb-4">{isEditMode ? 'Edit Item' : 'New Item'}</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block font-semibold mb-1">Item Name</label>
            <input
              type="text"
              name="name"
              value={item.name || ''}
              onChange={handleInputChange}
              className="border p-2 rounded w-full"
              required
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">SKU</label>
            <input
              type="text"
              name="sku"
              value={item.sku || ''}
              onChange={handleInputChange}
              className="border p-2 rounded w-full"
              required
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">Unit</label>
            <Select
              name="unit"
              value={unitsOption.find(c => c.value === item.unit) || null}
              onChange={(s) => setItem(prev => ({ ...prev, unit: s.value }))}
              options={unitsOption}
              placeholder="Select Unit"
              components={{ DropdownIndicator }}
            />
          </div>
        </div>

        {/* Track by (checkboxes) */}
        <div>
          <label className="block font-semibold mb-2">Track stock by</label>
          <div className="flex gap-6 items-center">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="accent-blue-600"
                checked={trackBySize}
                onChange={(e) => setTrackBySize(e.target.checked)}
              />
              <span>Size</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="accent-blue-600"
                checked={trackByColor}
                onChange={(e) => setTrackByColor(e.target.checked)}
              />
              <span>Color</span>
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            You can enable both. In purchases/sales, use either Size <em>or</em> Color per line to avoid double counting (no size×color variants).
          </p>
        </div>

        {/* Sizes editor */}
        {trackBySize && (
          <div>
            <div className="flex items-center justify-between">
              <label className="block font-semibold mb-1">Sizes</label>
              <button
                type="button"
                className="text-xs text-blue-600 underline"
                onClick={usePresetSizes}
                title="Load from preset sizes"
              >
                Use presets
              </button>
            </div>
            <div className="space-y-2">
              {(item.sizes || []).map((s, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <input
                    type="text"
                    placeholder="Size"
                    value={s.size}
                    onChange={(e) => handleSizeChange(index, 'size', e.target.value)}
                    className="border p-2 rounded w-full"
                    list="meta-sizes"
                  />
                  <datalist id="meta-sizes">
                    {metaDefaultSizes.map((n, i) => <option key={i} value={n} />)}
                  </datalist>
                  <input
                    type="number"
                    placeholder="Quantity"
                    value={s.quantity}
                    onChange={(e) => handleSizeChange(index, 'quantity', e.target.value)}
                    className="border p-2 rounded w-full"
                  />
                  <button type="button" onClick={() => removeSizeRow(index)}>
                    <TrashIcon className="w-5 h-5 text-red-600" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addSizeRow} className="text-blue-600">+ Add Size</button>
            </div>
          </div>
        )}

        {/* Colors editor */}
        {trackByColor && (
          <div>
            <div className="flex items-center justify-between">
              <label className="block font-semibold mb-1">Colors</label>
              <button
                type="button"
                className="text-xs text-blue-600 underline"
                onClick={usePresetColors}
                title="Load from preset colors"
              >
                Use presets
              </button>
            </div>
            <div className="space-y-2">
              {(item.colors || []).map((c, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <input
                    type="text"
                    placeholder="Color"
                    value={c.color}
                    onChange={(e) => handleColorChange(index, 'color', e.target.value)}
                    className="border p-2 rounded w-full"
                    list="meta-colors"
                  />
                  <datalist id="meta-colors">
                    {colorsOption.map((n, i) => <option key={i} value={n} />)}
                  </datalist>
                  <input
                    type="number"
                    placeholder="Quantity"
                    value={c.quantity}
                    onChange={(e) => handleColorChange(index, 'quantity', e.target.value)}
                    className="border p-2 rounded w-full"
                  />
                  <button type="button" onClick={() => removeColorRow(index)}>
                    <TrashIcon className="w-5 h-5 text-red-600" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addColorRow} className="text-blue-600">+ Add Color</button>
            </div>
          </div>
        )}

        {/* Category / Brand / Min qty */}
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block font-semibold mb-1">Category</label>
            <div className="flex items-center">
              <Select
                name="category_id"
                value={categoriesOption.find(c => c.value === item.category_id) || null}
                onChange={(s) => setItem(prev => ({ ...prev, category_id: s.value }))}
                options={categoriesOption}
                components={{ DropdownIndicator }}
                className="flex-1"
              />
              <button
                type="button"
                onClick={() => setShowCategoryModal(true)}
                className="ml-2 p-1"
              >
                <PlusIcon className="h-5 w-5 text-black" />
              </button>
            </div>
          </div>
          <div>
            <label className="block font-semibold mb-1">Brand</label>
            <div className="flex items-center">
              <Select
                name="brand_id"
                value={brandsOption.find(c => c.value === item.brand_id) || null}
                onChange={(s) => setItem(prev => ({ ...prev, brand_id: s.value }))}
                options={brandsOption}
                components={{ DropdownIndicator }}
                className="flex-1"
              />
              <button
                type="button"
                onClick={() => setShowBrandModal(true)}
                className="ml-2 p-1"
              >
                <PlusIcon className="h-5 w-5 text-black" />
              </button>
            </div>
          </div>
          <div>
            <label className="block font-semibold mb-1">Min Qty</label>
            <input
              type="number"
              name="min_qty"
              value={item.min_qty || 0}
              onChange={handleInputChange}
              className="border p-2 rounded w-full"
            />
          </div>
        </div>

        {/* Prices */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold mb-1">Purchase Price</label>
            <input
              type="number"
              name="purchase_price"
              value={item.purchase_price || ''}
              onChange={handleInputChange}
              className="border p-2 rounded w-full"
              required
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">Sales Price</label>
            <input
              type="number"
              name="sales_price"
              value={item.sales_price || ''}
              onChange={handleInputChange}
              className="border p-2 rounded w-full"
              required
            />
          </div>
        </div>

        <textarea
          name="description"
          value={item.description || ''}
          onChange={handleInputChange}
          rows="2"
          placeholder="Description"
          className="border p-2 rounded w-full"
        />

        {/* Image */}
        <div>
          <label className="block mb-1 font-medium">Upload Image</label>
          <input type="file" accept="image/*" onChange={handleImageChange} className="mb-2" />
          {imagePreview && <img src={imagePreview} alt="Preview" className="w-32 h-32 object-cover border" />}
        </div>

        <div className="text-right">
          <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700">
            {isEditMode ? 'Update Item' : 'Create Item'}
          </button>
        </div>
      </form>

      <AddCategoryBrandModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        type="category"
        onCreated={(newCat) => setCategoriesOption(prev => [...prev, { value: newCat.id, label: newCat.name }])}
      />
      <AddCategoryBrandModal
        isOpen={showBrandModal}
        onClose={() => setShowBrandModal(false)}
        type="brand"
        onCreated={(newBrand) => setBrandsOption(prev => [...prev, { value: newBrand.id, label: newBrand.name }])}
      />
      <Loader show={loading} />
    </div>
  );
};

export default NewItem;